import React, { useEffect, useMemo, useState } from "react";
import SubHeader from "components/SubHeader";
import SubHeaderTitle from "components/SubHeader/SubHeaderTitle";
import SubHeaderBackButton from "components/SubHeader/SubHeaderBackButton";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";

import { TCity, TWaterMeter } from "types/dto";
import { toast } from "react-toastify";
import { TWaterMetersGridRow } from "components/Grid/grids/WaterMetersGrid/types";
import { useParams } from "react-router-dom";

import { TApartmentGridRow } from "components/Grid/grids/ApartmentsGrid/types";
import { MeterInfo, TEditFormTypes } from "./components/MeterInfo";
import { MeterInstallationHistory } from "./components/MeterInstallationHistory";
import Tabs from "components/Tabs";
import { tabsData, TabsValue } from "./constants/tabsData";
import { MeterReadingData } from "./components/MeterReadingData";
import { MeterStatus } from "./components/MeterStatus";
import { MeterAlarms } from "./components/MeterAlarms";
import { BarChart } from "components/Charts/BarChart";
import { PieChart } from "components/Charts/PieChart";
import { LineChart } from "components/Charts/LineChart";
import { MeterReadingDataChart } from "./components/MeterReadingDataChart";
import { MeterAggregatedDataInRange } from "./components/MeterAggregatedDataInRange";
import DateRangePicker from "components/DateRangePicker";
import { useURLTools } from "utils/URLtools";
import CenterContainer from "components/CenterContainer";
import { Map_mapbox } from "components/Map";
import { Map_openLayers } from "components/Map_openLayers";
import { MeterLocationMap } from "./components/MeterLocationMap";
import { MeterSubscriberInfo } from "./components/MeterSubscriberInfo";
import { ValveStatus } from "./ValveStatus";
import { TResponseCode } from "utils/netCall/types";

const MeterDetailsView = () => {
  const { t } = useTranslation();
  const { meterId } = useParams();
  const { queryParams, addUpdateRemoveQueryParams } = useURLTools();

  const [isGettingData, setIsGettingData] = useState<boolean>(false);
  const [initData, setInitData] = useState<TWaterMeter>();
  const [meterInfoSubmitting, setMeterInfoSubmitting] = useState(false);
  const [isApartmentsModalOpen, setIsApartmentsModalOpen] = useState(false);
  const [selectedApartment, setSelectedApartment] =
    useState<TApartmentGridRow>();
  const [selectedMeter, setSelectedMeter] = useState<TWaterMetersGridRow>();
  const [isMetersModalOpen, setIsMetersModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<
    "apartment" | "assignMeter" | "unassignMeter"
  >();
  const [confirmationSubmitting, setConfirmationSubmitting] = useState(false);
  const [selectedTab, setSelectedTab] = useState<TabsValue>(tabsData[0].value);
  const [enableEditing, setEnableEditing] = useState(false);

  const confirmationModalDesc = useMemo(() => {
    if (confirmationModal === "apartment") {
      return t("confirmChangeApartmentDesc");
    } else if (confirmationModal === "assignMeter") {
      return t("confirmChangeMeterDesc");
    } else if (confirmationModal === "unassignMeter") {
      return t("confirmUnAssignMeterDesc");
    }
  }, [confirmationModal]);

  const onChooseApartment = (value: TApartmentGridRow) => {
    setSelectedApartment(value);
    setConfirmationModal("apartment");
  };
  const onChooseMeter = (value: TWaterMetersGridRow) => {
    setSelectedMeter(value);
    setConfirmationModal("assignMeter");
  };

  const getMeterDetails = async () => {
    setIsGettingData(true);
    const res = await netCall<TWaterMeter>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterMeter/GetWaterMeter`,
      body: {
        waterMeterId: meterId,
        startDT: queryParams?.from,
        endDT: queryParams?.to,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setInitData(res.results);
    } else {
      toast.error(t("errorInGettingData"));
    }
    setIsGettingData(false);
  };

  const onSaveMeterInfo = async (values: TEditFormTypes) => {
    setMeterInfoSubmitting(true);
    const res = await netCall<TCity>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterMeter/UpdateWaterMeter`,
      body: {
        id: initData?.id,
        ...values,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setMeterInfoSubmitting(false);
      toast.success(t("operationSuccessful"));
      await getMeterDetails();
      setEnableEditing(false);
    } else {
      setMeterInfoSubmitting(false);
      toast.error(t("operationFailed"));
    }
  };

  // const submitChangeMeter = async () => {
  //   setConfirmationSubmitting(true);
  //   const res: RequestResponseResult<any> = await netCall({
  //     method: "POST",
  //     endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/AddWaterMeter2WaterSubscribe`,
  //     data: {
  //       payloadData: {
  //         waterMeterId: selectedMeter?.id,
  //         waterSubscriberId: initData?.id,
  //       },
  //     },
  //   });
  //   if (res.responseCode === ResponseCode.OK) {
  //     setConfirmationSubmitting(false);
  //     setIsMetersModalOpen(false);
  //     setConfirmationModal(undefined);
  //     toast.success(t("operationSuccessful"));
  //     await getSubscriberDetails();
  //   } else {
  //     setConfirmationSubmitting(false);
  //     toast.error(t("operationFailed"));
  //   }
  // };
  // const submitChangeApartment = async () => {
  //   setConfirmationSubmitting(true);
  //   const res: RequestResponseResult<any> = await netCall({
  //     method: "POST",
  //     endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/Update`,
  //     data: {
  //       payloadData: {
  //         ...omit(initData, [
  //           "apartmentId",
  //           "currentWaterMeter",
  //           "installationDT",
  //           "historicalWaterMeters",
  //         ]),
  //         apartmentId: selectedApartment?.id,
  //       },
  //     },
  //   });
  //   if (res.responseCode === ResponseCode.OK) {
  //     setConfirmationSubmitting(false);
  //     setIsApartmentsModalOpen(false);
  //     setConfirmationModal(undefined);
  //     toast.success(t("operationSuccessful"));
  //     await getSubscriberDetails();
  //   } else {
  //     setConfirmationSubmitting(false);
  //     toast.error(t("operationFailed"));
  //   }
  // };
  // const submitUnassignMeter = async () => {
  //   setConfirmationSubmitting(true);
  //   const res: RequestResponseResult<any> = await netCall({
  //     method: "POST",
  //     endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/RemoveWaterMeterFromWaterSubscribe`,
  //     data: {
  //       payloadData: {
  //         waterMeterId: initData?.waterMeterId,
  //         waterSubscriberId: initData?.id,
  //       },
  //     },
  //   });
  //   if (res.responseCode === ResponseCode.OK) {
  //     setConfirmationSubmitting(false);
  //     setConfirmationModal(undefined);
  //     toast.success(t("operationSuccessful"));
  //     await getSubscriberDetails();
  //   } else {
  //     setConfirmationSubmitting(false);
  //     toast.error(t("operationFailed"));
  //   }
  // };
  //
  // const onSubmitConfirmation = () => {
  //   if (confirmationModal === "apartment") {
  //     submitChangeApartment();
  //   } else if (confirmationModal === "assignMeter") {
  //     submitChangeMeter();
  //   } else if (confirmationModal === "unassignMeter") {
  //     submitUnassignMeter();
  //   }
  // };

  useEffect(() => {
    if (meterId && queryParams?.from && queryParams?.to) {
      getMeterDetails();
    }
  }, [meterId, queryParams]);

  useEffect(() => {
    if (meterId && !queryParams?.from && !queryParams?.to) {
      const today = new Date();
      const priorDate = new Date().setDate(today.getDate() - 7);
      addUpdateRemoveQueryParams([
        {
          parameter: "from",
          value: new Date(priorDate).toISOString().split("T")[0],
        },
        { parameter: "to", value: today.toISOString().split("T")[0] },
      ]);
    }
  }, [meterId, queryParams]);

  const renderTabContent = () => {
    if (selectedTab === TabsValue.OVERVIEW) {
      return (
        <>
          <div className={"w-full my-4 pr-4"}>
            <DateRangePicker
              initFromDate={queryParams?.from}
              initToDate={queryParams?.to}
              onChange={(value) => {
                if (value[0]) {
                  addUpdateRemoveQueryParams([
                    { parameter: "from", value: value[0]?.split("T")[0] },
                  ]);
                }
                if (value[1]) {
                  addUpdateRemoveQueryParams([
                    { parameter: "to", value: value[1]?.split("T")[0] },
                  ]);
                }
              }}
              disabled={isGettingData}
            />
          </div>
          {initData?.readingData?.readingData &&
          initData?.readingData?.readingData.length > 0 ? (
            <div
              className={
                "grid grid-cols-3 gap-y-4 px-4 pb-4 h-full overflow-auto gap-x-4"
              }
            >
              <div className={"col-span-1 "}>
                <MeterAggregatedDataInRange
                  data={initData.readingData}
                  isLoading={isGettingData}
                />
              </div>
              <div className={"col-span-2 "}>
                <MeterReadingDataChart
                  data={initData.readingData}
                  isLoading={isGettingData}
                />
              </div>
              <div className={"col-span-1"}>
                <MeterStatus data={initData} isLoading={isGettingData} />
                {/*<div className={"mt-4"}>*/}
                {/*  <ValveStatus />*/}
                {/*</div>*/}
              </div>
              <div className={"col-span-2"}>
                <MeterAlarms
                  data={initData.readingData}
                  isLoading={isGettingData}
                />
              </div>
              <div className={"col-span-3"}>
                <MeterReadingData
                  data={initData.readingData}
                  isLoading={isGettingData}
                />
              </div>
            </div>
          ) : (
            <CenterContainer>
              <p className={"text-base"}>
                {t("noDataAvailableForSelectedDateRange")}
              </p>
            </CenterContainer>
          )}
        </>
      );
    } else if (selectedTab === TabsValue.INFO) {
      return (
        <div
          className={
            "grid grid-cols-3 w-full gap-y-4 gap-x-3 p-4 overflow-auto"
          }
        >
          <div className="col-span-2">
            <MeterInfo
              data={initData}
              isLoading={isGettingData}
              onSaveClick={onSaveMeterInfo}
              isButtonLoading={meterInfoSubmitting}
              onEditClick={() => setEnableEditing(true)}
              enableEditing={enableEditing}
            />
          </div>
          <div className={"col-span-1"}>
            <MeterLocationMap data={initData} />
          </div>
          <div className="col-span-3">
            <MeterSubscriberInfo
              waterSubscriberId={
                initData?.currentInstallation?.waterSubscriberId
              }
              identifierId={initData?.currentInstallation?.identifierId}
              installationDT={initData?.currentInstallation?.installationDT}
              isLoading={isGettingData}
            />
          </div>
          <div className="col-span-3">
            <MeterInstallationHistory
              data={initData}
              isLoading={isGettingData}
            />
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <SubHeader>
        <SubHeaderTitle>
          <SubHeaderBackButton />
          {t("meterDetails")}
        </SubHeaderTitle>
      </SubHeader>
      <Tabs
        tabs={tabsData}
        value={selectedTab}
        onChange={setSelectedTab}
        variant={"fullWidth"}
      />
      {renderTabContent()}
    </>
  );
};

export default MeterDetailsView;
